import React, { useCallback, useMemo } from 'react';
import { Setting, SettingInfo, SettingsSectionSubHeading } from 'components/Setting/style';
import { ParagraphXSmallRegular } from 'components/Typography';
import { CustomMessageToggleSettingDetail, CustomMessageToggleWrapper } from 'components/CustomLandingPageMessage/style';
import { Tooltip } from 'components/Tooltip';
import _debounce from 'lodash/debounce';
import { Toggle } from '@econsult/econsult-ui/dist/components/Toggle';
import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast } from 'components/Toast';
import { STRINGS } from 'config';
import { saveCustomLandingPageMessageAndVisibility } from 'store/action/landingPageMessage';

const { PENDING, SUCCEEDED, FAILED } = STRINGS.REQUEST_STATUS;

export const ShowCustomLandingPageMessageToggle = props => {
  const {
    message,
    showMessage,
    selectedPractice,
    selectedPracticeHasAdminRights,
    maxMessageLength,
    isToggleDisabled,
    setShowMessage,
    setSaveRequestStatus,
    isGetRequestPending,
    isSaveRequestPending,
  } = props;

  const onToggleShowMessage = useCallback(() => {
    if (!selectedPractice || isToggleDisabled || isGetRequestPending || isSaveRequestPending) {
      return null;
    }

    setShowMessage(value => !value);

    if (message.length > maxMessageLength) {
      return toast(<ErrorToast message="Message is too long." />);
    }

    if (!message.length) {
      return toast(<ErrorToast message="No custom message configured" />);
    }

    setSaveRequestStatus(PENDING);

    return saveCustomLandingPageMessageAndVisibility({
      practiceId: selectedPractice.id,
      showMessage: !showMessage,
      message: message.trim(),
    })
      .then(isSuccess => {
        if (!isSuccess) {
          return setSaveRequestStatus(FAILED);
        }

        return setSaveRequestStatus(SUCCEEDED);
      });
  }, [
    isGetRequestPending,
    isSaveRequestPending,
    isToggleDisabled,
    maxMessageLength,
    message,
    selectedPractice,
    setSaveRequestStatus,
    setShowMessage,
    showMessage,
  ]);

  const debouncedToggle = useMemo(() => _debounce(onToggleShowMessage, 2_000, { leading: true }), [onToggleShowMessage]);

  return (
    <Setting>
      <SettingInfo>
        <SettingsSectionSubHeading>
          Visibility
        </SettingsSectionSubHeading>

        <ParagraphXSmallRegular>
          Activate or deactivate the custom message.
        </ParagraphXSmallRegular>
      </SettingInfo>

      <CustomMessageToggleSettingDetail>
        <CustomMessageToggleWrapper>
          <Toggle
            id="showCustomLandingPageMessageToggle"
            name="showCustomLandingPageMessageToggle"
            isChecked={showMessage}
            onClick={debouncedToggle}
            label="Show custom message on landing page"
            disabled={isToggleDisabled}
          />

          {isToggleDisabled && (
            <Tooltip
              automationLabel="ShowCustomLandingPageMessageToggleTooltip"
              label={!selectedPracticeHasAdminRights ? 'Contact your practice admin' : 'No custom message configured'}
              align="left"
            />
          )}
        </CustomMessageToggleWrapper>
      </CustomMessageToggleSettingDetail>
    </Setting>
  );
};
